import { InAppNotification } from '@src/api/types/inAppNotification';
import { useCompanyInAppNotificationPolling } from '@src/hooks/useCompanyInAppNotificationPolling';
import { useTenantInAppNotificationPolling } from '@src/hooks/useTenantInAppNotificationPolling';
import { fetchNotifications as fetchCompanyNotifications } from '@src/store/company/companyInAppNotificationSlice';
import { AppDispatch } from '@src/store/store';
import { fetchNotifications as fetchTenantNotifications } from '@src/store/tenant/tenantInAppNotificationSlice';
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';

import { useSharedAuth } from './auth/useSharedAuth';

type InAppNotificationContextType = {
  notifications: InAppNotification[];
  isExistNewNotification: boolean;
  resetNewNotification: () => void;
  refetchNotifications: () => void;
};

const InAppNotificationContext = createContext<InAppNotificationContextType | undefined>(undefined);

export const InAppNotificationProvider = ({ children }: { children: ReactNode }) => {
  const { profile } = useSharedAuth();
  const dispatch = useDispatch<AppDispatch>();

  const isTenant = profile?.isTenant ?? false;
  const isCompany = !isTenant;
  const isLogin = profile?.isTenant
    ? !!profile?.tenantProfile?.isLogin
    : !!profile?.companyProfile?.isLogin;

  const tenantNotifications = useTenantInAppNotificationPolling(isTenant && isLogin);
  const companyNotifications = useCompanyInAppNotificationPolling(isCompany && isLogin);

  const refetchNotifications = useCallback(() => {
    if (!isLogin) {
      return;
    }

    if (isTenant) {
      dispatch(fetchTenantNotifications());
    } else {
      dispatch(fetchCompanyNotifications());
    }
  }, [isTenant, isLogin, dispatch]);

  const contextValue: InAppNotificationContextType = {
    notifications: isTenant
      ? tenantNotifications.notifications
      : companyNotifications.notifications,
    isExistNewNotification: isTenant
      ? tenantNotifications.isExistNewNotification
      : companyNotifications.isExistNewNotification,
    resetNewNotification: isTenant
      ? tenantNotifications.resetNewNotification
      : companyNotifications.resetNewNotification,
    refetchNotifications,
  };

  return (
    <InAppNotificationContext.Provider value={contextValue}>
      {children}
    </InAppNotificationContext.Provider>
  );
};

export const useInAppNotification = () => {
  const context = useContext(InAppNotificationContext);
  if (!context) {
    throw new Error('useInAppNotification must be used within an InAppNotificationProvider');
  }
  return context;
};
